import { addMinutes, isBefore, isValid, max, parse, setHours, setMinutes } from 'date-fns';
import { formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz';
import { LOCAL_DATE_TIME_WITH_MILLISECONDS_FORMAT } from '../constants';
import { startOfOmnidots } from './date-fns';
import { getBrowserTimezone } from './get-browser-timezone';

export const DAY_START_TIME = '00:00';
export const DAY_END_TIME = '23:59';

type DateRange = {
    startDate: Date;
    endDate: Date;
};

type ParsedDateRange = {
    startDate: Date | null;
    endDate: Date | null;
};

type DateRangeValues = {
    startDate: string;
    endDate: string;
};

export function applyTimeString(date: Date, timeString?: string | null): Date {
    if (timeString && !isValid(parse(timeString, 'HH:mm', new Date()))) {
        throw new Error(`Invalid time value: ${timeString}`);
    }

    const [hours, minutes] = timeString ? timeString.split(':') : [0, 0];
    return setMinutes(setHours(date, Number(hours)), Number(minutes));
}

export function ensureChronologicalDateRangeInTimezone(
    { startDate, endDate }: DateRange,
    timezone: string
): DateRange {
    if (!isBefore(endDate, startDate)) {
        return { startDate, endDate };
    }

    // Correct the instant in UTC. Manipulating utcToZonedTime's pseudo-local
    // dates would apply the browser's DST rules instead of the requested timezone's.
    const correctedEndDate = addMinutes(startDate, 1);
    const endDateInTimezone = formatInTimeZone(endDate, timezone, 'yyyy-MM-dd');
    const correctedEndDateInTimezone = formatInTimeZone(correctedEndDate, timezone, 'yyyy-MM-dd');

    // Preserve the selected local days when the one-minute correction crosses
    // midnight in the requested timezone.
    if (endDateInTimezone !== correctedEndDateInTimezone) {
        const startDateInTimezone = formatInTimeZone(startDate, timezone, 'yyyy-MM-dd');
        const dayBoundedRange = {
            startDate: zonedTimeToUtc(`${startDateInTimezone}T${DAY_START_TIME}:00.000`, timezone),
            endDate: zonedTimeToUtc(`${endDateInTimezone}T${DAY_END_TIME}:59.999`, timezone),
        };

        if (!isBefore(dayBoundedRange.endDate, dayBoundedRange.startDate)) {
            return dayBoundedRange;
        }
    }

    return { startDate, endDate: correctedEndDate };
}

export function ensureChronologicalDateRange(dateRange: DateRange): DateRange {
    return ensureChronologicalDateRangeInTimezone(dateRange, getBrowserTimezone());
}

export function parseDateTimeInTimezone(value: string, timezone: string): Date | null {
    if (!value) {
        return null;
    }

    const date = zonedTimeToUtc(value, timezone);
    return isValid(date) ? date : null;
}

function parseAndNormalizeDateRangeWith(
    { startDate, endDate }: DateRangeValues,
    parseDateTime: (value: string) => Date | null,
    normalizeDateRange: (dateRange: DateRange) => DateRange,
    minimumDate: Date
): ParsedDateRange {
    function parseBound(value: string): Date | null {
        const date = parseDateTime(value);
        return date ? max([date, minimumDate]) : null;
    }

    const parsedStartDate = parseBound(startDate);
    const parsedEndDate = parseBound(endDate);

    return parsedStartDate && parsedEndDate
        ? normalizeDateRange({ startDate: parsedStartDate, endDate: parsedEndDate })
        : { startDate: parsedStartDate, endDate: parsedEndDate };
}

export function parseAndNormalizeDateRange(dateRange: DateRangeValues): ParsedDateRange {
    return parseAndNormalizeDateRangeWith(
        dateRange,
        (value) => {
            const date = new Date(value);
            return isValid(date) ? date : null;
        },
        ensureChronologicalDateRange,
        startOfOmnidots
    );
}

export function formatDateTimeInTimezone(date: Date, timezone: string): string {
    return formatInTimeZone(date, timezone, LOCAL_DATE_TIME_WITH_MILLISECONDS_FORMAT);
}

export function parseAndNormalizeDateRangeInTimezone(
    dateRange: DateRangeValues,
    timezone: string
): ParsedDateRange {
    return parseAndNormalizeDateRangeWith(
        dateRange,
        (value) => parseDateTimeInTimezone(value, timezone),
        (parsedRange) => ensureChronologicalDateRangeInTimezone(parsedRange, timezone),
        zonedTimeToUtc(startOfOmnidots, timezone)
    );
}
